import React from 'react';
import { Wrench, Settings, CheckCircle, Phone, ArrowRight, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const MaintenanceIndustrielle = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <SEO 
        title="Maintenance Industrielle sur Site | Intervention en France | ORION INDUSTRIE"
        description="Maintenance préventive et curative d'installations industrielles. Réparation d'équipements, maintenance mécanique, intervention sur machines industrielles. Depuis la Nièvre, intervention nationale."
        keywords="maintenance industrielle, maintenance mécanique, réparation équipement industriel, maintenance préventive, maintenance curative, intervention site industriel"
      />
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#020d2a] to-slate-800 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Settings className="w-12 h-12 text-[#FF6600]" />
            <h1 className="text-4xl sm:text-5xl font-bold">
              Maintenance Industrielle
            </h1>
          </div>
          <p className="text-2xl text-orange-400 font-semibold mb-4">
            Maintenance préventive & curative • Intervention sur site
          </p>
          <p className="text-xl text-slate-300 max-w-4xl mb-8">
            Nous intervenons directement sur vos installations industrielles pour réaliser des travaux de maintenance, 
            de réparation et de remise en état d'équipements mécaniques et structures métalliques.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button 
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              className="bg-[#FF6600] hover:bg-[#E65A00] text-white group"
            >
              Demander une intervention
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <a href="tel:+33659452562">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-gray-300 text-white hover:bg-white/10"
              >
                <Phone className="mr-2 w-5 h-5" />
                06 59 45 25 62
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Nos prestations */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">
            Nos Prestations de Maintenance Industrielle
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Wrench className="w-8 h-8 text-blue-600" />,
                title: 'Maintenance Préventive',
                desc: 'Interventions programmées pour prévenir les pannes et maintenir vos équipements en bon état de fonctionnement.',
                items: [
                  'Inspections périodiques',
                  'Contrôles de fonctionnement',
                  'Graissage et lubrification',
                  'Réglages et ajustements',
                  'Identification des usures'
                ]
              },
              {
                icon: <Wrench className="w-8 h-8 text-orange-600" />,
                title: 'Maintenance Curative',
                desc: 'Interventions correctives pour réparer vos équipements en panne ou défaillants.',
                items: [
                  'Diagnostic de panne',
                  'Réparation d\'équipements',
                  'Remplacement de pièces',
                  'Remise en service',
                  'Tests de fonctionnement'
                ]
              },
              {
                icon: <Settings className="w-8 h-8 text-purple-600" />,
                title: 'Maintenance Mécanique',
                desc: 'Travaux de maintenance sur éléments mécaniques, structures et composants industriels.',
                items: [
                  'Organes de transmission',
                  'Paliers et roulements',
                  'Systèmes d\'entraînement',
                  'Éléments de structure',
                  'Assemblages mécaniques'
                ]
              },
              {
                icon: <Wrench className="w-8 h-8 text-green-600" />,
                title: 'Réparation d\'Équipements',
                desc: 'Remise en état d\'équipements industriels défaillants ou endommagés.',
                items: [
                  'Convoyeurs et manutention',
                  'Équipements de levage',
                  'Machines de production',
                  'Installations fixes',
                  'Matériel de chantier'
                ]
              },
              {
                icon: <Settings className="w-8 h-8 text-teal-600" />,
                title: 'Intervention sur Machines',
                desc: 'Maintenance et réparation de machines industrielles et équipements de production.',
                items: [
                  'Diagnostic technique',
                  'Réparation mécanique',
                  'Remplacement composants',
                  'Remise en conformité',
                  'Optimisation fonctionnement'
                ]
              },
              {
                icon: <Settings className="w-8 h-8 text-red-600" />,
                title: 'Remise en État',
                desc: 'Rénovation et remise en état d\'éléments mécaniques et structures métalliques.',
                items: [
                  'Évaluation de l\'état',
                  'Nettoyage et préparation',
                  'Réparation ou remplacement',
                  'Traitement anticorrosion',
                  'Essais et validation'
                ]
              }
            ].map((service, index) => (
              <div key={index} className="bg-slate-50 rounded-xl p-6 hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-white rounded-lg flex items-center justify-center mb-4 shadow">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600 mb-4 text-sm">{service.desc}</p>
                <ul className="space-y-2">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Notre approche */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">
              Notre Approche
            </h2>

            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <span className="text-[#FF6600]">1.</span>
                  Diagnostic et Évaluation
                </h3>
                <p className="text-slate-700 mb-4">
                  Nous analysons l'état de vos équipements, identifions les défaillances et évaluons les solutions techniques adaptées.
                </p>
                <ul className="space-y-2">
                  {[
                    'Inspection visuelle et fonctionnelle',
                    'Identification des causes de défaillance',
                    'Évaluation des solutions possibles',
                    'Recommandations techniques'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <span className="text-[#FF6600]">2.</span>
                  Intervention sur Site
                </h3>
                <p className="text-slate-700 mb-4">
                  Nous intervenons directement chez vous avec le matériel et les compétences nécessaires pour réaliser les travaux.
                </p>
                <ul className="space-y-2">
                  {[
                    'Intervention avec outillage adapté',
                    'Respect des consignes de sécurité',
                    'Coordination avec vos équipes',
                    'Adaptation aux contraintes d\'exploitation'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <span className="text-[#FF6600]">3.</span>
                  Remise en Service et Suivi
                </h3>
                <p className="text-slate-700 mb-4">
                  Après l'intervention, nous nous assurons du bon fonctionnement et documentons les travaux réalisés.
                </p>
                <ul className="space-y-2">
                  {[
                    'Tests de fonctionnement',
                    'Validation de la remise en service',
                    'Rapport d\'intervention',
                    'Recommandations pour l\'entretien futur'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <MapPin className="w-12 h-12 text-[#FF6600] mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Zone d'Intervention
            </h2>
            <p className="text-xl text-slate-700 mb-6">
              <strong>ORION INDUSTRIE</strong> est basée à <strong>Nevers, dans la Nièvre (58)</strong>.
            </p>
            <p className="text-lg text-slate-600 mb-8">
              Nous intervenons sur des sites industriels partout en France selon la nature des travaux, 
              leur durée et les conditions d'intervention. Les frais de déplacement sont intégrés au devis 
              en fonction de la localisation du site et de la durée de l'intervention.
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-lg text-left">
              <p className="text-slate-700">
                Chaque projet fait l'objet d'une étude technique et tarifaire adaptée à vos besoins spécifiques.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="py-20 bg-gradient-to-r from-[#020d2a] to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Besoin d'une intervention de maintenance industrielle ?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Contactez-nous pour échanger sur votre projet
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => window.location.href = '/#quote'}
              size="lg"
              className="bg-[#FF6600] hover:bg-[#E65A00] text-white"
            >
              Demander un devis
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a href="tel:+33659452562">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10"
              >
                <Phone className="mr-2 w-5 h-5" />
                06 59 45 25 62
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MaintenanceIndustrielle;